package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.ThreadLocalRef
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.jvm.JvmStatic

/**
 * 响应式上下文：维护当前线程的依赖追踪栈以及 Effect 执行深度计数。
 */
object ReactiveContext {

    /** 追踪帧：订阅者 + 依赖收集集合。 */
    class Frame(val subscriber: Subscriber, val deps: MutableSet<Trackable>)

    /** Effect 重跑深度阈值。 */
    internal const val MAX_EFFECT_DEPTH = 100

    private val trackerStack: ThreadLocalRef<ArrayDeque<Frame>> = ThreadLocalRef { ArrayDeque() }
    private val effectDepth: ThreadLocalRef<IntArray> = ThreadLocalRef { intArrayOf(0) }
    private val logger = UiLog("top.wcpe.allincore.ui.runtime.reactive.ReactiveContext")

    @JvmStatic fun pushTracker(frame: Frame) {
        trackerStack.get().addFirst(frame)
    }

    @JvmStatic
    fun popTracker() {
        val stack = trackerStack.get()
        if (stack.isEmpty()) throw IllegalStateException("popTracker 被调用但追踪栈为空")
        stack.removeFirst()
    }

    @JvmStatic
    fun track(trackable: Trackable) {
        val top = trackerStack.get().firstOrNull() ?: return
        if (top.deps.add(trackable)) trackable.subscribe(top.subscriber)
    }

    @JvmStatic
    fun currentEffectRunCount(): Int {
        val top = trackerStack.get().firstOrNull() ?: return -1
        if (top.subscriber is Effect) return (top.subscriber as Effect).runCount()
        return -1
    }

    @JvmStatic
    fun currentTrackedDeps(): MutableList<Trackable>? {
        val top = trackerStack.get().firstOrNull() ?: return null
        return ArrayList(top.deps)
    }

    @JvmStatic
    fun enterEffect(): Boolean {
        val slot = effectDepth.get()
        val next = slot[0] + 1
        if (next > MAX_EFFECT_DEPTH) {
            logger.error(
                "[ui] 检测到疑似响应式循环：Effect 重跑深度超过阈值 $MAX_EFFECT_DEPTH（当前深度 ${slot[0]}），已停止本次重跑",
            )
            return false
        }
        slot[0] = next
        return true
    }

    @JvmStatic
    fun exitEffect() {
        val slot = effectDepth.get()
        if (slot[0] > 0) slot[0]--
    }
}
